import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useEffectOnce from "../hooks/useEffectOnce";
import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";

// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
import TaskImages from "../components/TaskImages";
import { commitTaskImages } from "../utils/taskImages";
import TextInput from "../components/TextInput";
import SelectInput from "../components/SelectInput";
import DateInput from "../components/DateInput";
import Loader from "../components/Loader";
import "../assets/css/modal.css";
import "../assets/css/reportTable.css";
import "../assets/css/tasks.css";

// --- Redux Selectors ---
import {
  selectTasks,
  selectTasksStatus,
  selectCreateTaskStatus,
  selectprojectMenu,
  selectClientMenu,
  selectClientStatus,
  selectMainMenu,
  selectMainMenuStatus,
  selectSubmenu1,
  selectSubmenu1Status,
  selectSubmenu2,
  selectSubmenu2Status,
  selectSubmenu3,
  selectSubmenu3Status,
  selectSubmenu4,
  selectSubmenu4Status,
  selectGetRaisedbyDetailsList,
  selectQueryFrom,
} from "../redux/tasks/taskSelectors";

// --- Redux Actions ---
import {
  tasksApis,
  createTasksApis,
  projectMenuApis,
  queryFromApis,
  clientMenuApis,
  mainMenuApis,
  submenu1Apis,
  submenu2Apis,
  submenu3Apis,
  submenu4Apis,
  raisedbyDetailsApis,
} from "../redux/tasks/taskActions";

import { teamListApis } from "../redux/team/teamActions";
import { selectTeams } from "../redux/team/teamSelectors";
import TeamBadge from "../components/TeamBadge";
import { formatDateOnly, targetDateState, toDateOnly } from "../utils/common";
import { useTabRefresh } from "../context/TabsContext";
import TargetDateCell from "../components/TargetDateCell";
import TargetDateChips from "../components/TargetDateChips";
import useTargetDateFilter from "../hooks/useTargetDateFilter";

const Tasks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==============================
  // 1. LOCAL STATE
  // ==============================
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  /* The task being edited, so its screenshots can be loaded and changed in
     place. Null while creating — there is no row to attach anything to yet. */
  const [editQueryCode, setEditQueryCode] = useState(null);

  /* This form's screenshot changes, held until it is saved: pendingImages are
     files chosen and not yet sent, removedImages the QueryImageCodes of saved
     images the person dropped.

     They live here rather than inside TaskImages because they are part of what
     Update commits and what Cancel throws away — the same rule the rest of the
     fields follow. Before this, the control wrote as you clicked, and removing
     an image then pressing Cancel deleted it anyway. handleSubmit hands both
     lists to commitTaskImages once the task is written and has a code. */
  const [pendingImages, setPendingImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [activeTab, setActiveTab] = useState("manual");
  const [isLoading, setIsLoading] = useState(false);
  const [showRaisedByMenu, setShowRaisedByMenu] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Incomplete");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);

  // --- NEW STATE for Filter Badge ---
  const [filteredRecordCount, setFilteredRecordCount] = useState(0);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Form Data
  const initialFormState = {
    queryNo: "",
    project: "",
    client: "",
    /* Where the query came from — CLIENT SIDE or OWN. Blank rather than
       pre-set to 1: the field is required, and a default would let somebody
       save "CLIENT SIDE" without ever having said so. */
    queryFrom: "",
    menu: "",
    submenu1: "",
    submenu2: "",
    submenu3: "",
    submenu4: "",
    raisedBy: "",
    contactNo: "",
    taskName: "",
    description: "",
    priority: "",
    queryType: "",
    /* When the task should be done by. Optional here — a query is often
       raised before anybody knows — and compulsory on the Assignment
       screen, which is where a date entered here turns up already filled
       in. */
    targetDate: "",
    taskStatus: "Not Started",
    // Which team will own the task. Required — a task raised with no team is
    // invisible to everyone except a super admin.
    teamCode: "",
  };
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const lastRequestRef = useRef(null);
  /* Bumped when this tab is re-activated — see useTabRefresh below. */
  const [reloadKey, setReloadKey] = useState(0);

  // ==============================
  // 2. REDUX STATE
  // ==============================
  const tasksData = useSelector(selectTasks);
  const projectMenuList = useSelector(selectprojectMenu);
  const queryFromList = useSelector(selectQueryFrom);
  const clientMenuList = useSelector(selectClientMenu);
  const mainMenuList = useSelector(selectMainMenu);
  const submenu1List = useSelector(selectSubmenu1);
  const submenu2List = useSelector(selectSubmenu2);
  const submenu3List = useSelector(selectSubmenu3);
  const submenu4List = useSelector(selectSubmenu4);
  const submenu1Status = useSelector(selectSubmenu1Status);
  const submenu2Status = useSelector(selectSubmenu2Status);
  const submenu3Status = useSelector(selectSubmenu3Status);
  const submenu4Status = useSelector(selectSubmenu4Status);
  const raisedByList = useSelector(selectGetRaisedbyDetailsList);
  const teamList = useSelector(selectTeams);

  const listStatus = useSelector(selectTasksStatus);
  const createStatus = useSelector(selectCreateTaskStatus);

  // ==============================
  // 3. DATA PROCESSING
  // ==============================
  const filteredRaisedByList = useMemo(() => {
    if (!Array.isArray(raisedByList)) return [];
    if (!formData.raisedBy) return raisedByList;
    return raisedByList.filter((user) =>
      user.Client_UserName?.toLowerCase().includes(
        formData.raisedBy.toLowerCase(),
      ),
    );
  }, [raisedByList, formData.raisedBy]);

  const processedData = useMemo(() => {
    const rawList = Array.isArray(tasksData)
      ? tasksData
      : tasksData?.data?.data || tasksData?.data || [];
    if (!Array.isArray(rawList)) return [];

    return rawList.map((item) => {
      const fullClientName =
        item.ClientName || item.CompanyName || item.FormName || "-";
      return {
        ...item,
        DisplayClient: fullClientName,
        FormattedDate: item.QueryDate
          ? new Date(item.QueryDate).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          : "-",
        /* Two shapes of the same day: the ISO one sorts and filters, the
           written one is read. */
        TargetDateValue: toDateOnly(item.TargetDate),
        FormattedTargetDate: formatDateOnly(item.TargetDate),
      };
    });
  }, [tasksData]);

  // Dropdown Options
  const queryFromOptions = useMemo(
    () =>
      (Array.isArray(queryFromList) ? queryFromList : queryFromList?.data || [])
        .map((i) => ({
          value: i.QueryFromCode,
          label: i.QueryFrom,
        })),
    [queryFromList],
  );

  const projectOptions = useMemo(
    () =>
      (Array.isArray(projectMenuList)
        ? projectMenuList
        : projectMenuList?.data || []
      ).map((i) => ({
        value: i.ProjectCode || i.ProjectID || i.id,
        label: i.ProjectName || i.Name,
      })),
    [projectMenuList],
  );
  const clientOptions = useMemo(
    () =>
      (Array.isArray(clientMenuList)
        ? clientMenuList
        : clientMenuList?.data || []
      ).map((i) => ({
        value: i.ClientCode || i.ClientID || i.id,
        label: i.ClientName || i.Name,
      })),
    [clientMenuList],
  );
  const menuOptions = useMemo(
    () =>
      (Array.isArray(mainMenuList)
        ? mainMenuList
        : mainMenuList?.data || []
      ).map((i) => ({
        value: i.MenuCode || i.MenuID || i.id,
        label: i.FormName || i.Name,
      })),
    [mainMenuList],
  );
  // Fallback shown ONLY when the submenu API has finished and returned no values.
  // value "None" is a real selection so it satisfies the required check;
  // it is converted to null in the submit payload (see handleSubmit) and the
  // dependent fetch effects skip it so no further API is called.
  const NONE_OPTION = { value: "None", label: "None" };
  const mapSubmenu = (list) =>
    (Array.isArray(list) ? list : list?.data || []).map((i) => ({
      value: i.MenuCode || i.MenuID || i.id,
      label: i.FormName || i.Name,
    }));
  const submenu1Options = useMemo(() => {
    const opts = mapSubmenu(submenu1List);
    if (opts.length > 0) return opts;
    return submenu1Status === "succeeded" ? [NONE_OPTION] : [];
  }, [submenu1List, submenu1Status]);
  const submenu2Options = useMemo(() => {
    const opts = mapSubmenu(submenu2List);
    if (opts.length > 0) return opts;
    return submenu2Status === "succeeded" ? [NONE_OPTION] : [];
  }, [submenu2List, submenu2Status]);
  const submenu3Options = useMemo(() => {
    const opts = mapSubmenu(submenu3List);
    if (opts.length > 0) return opts;
    return submenu3Status === "succeeded" ? [NONE_OPTION] : [];
  }, [submenu3List, submenu3Status]);
  const submenu4Options = useMemo(() => {
    const opts = mapSubmenu(submenu4List);
    if (opts.length > 0) return opts;
    return submenu4Status === "succeeded" ? [NONE_OPTION] : [];
  }, [submenu4List, submenu4Status]);

  const priorityOptions = useMemo(
    () => ["High", "Medium", "Low"].map((p) => ({ value: p, label: p })),
    [],
  );
  const queryTypeOptions = useMemo(
    () => [
      { value: "Bug", label: "Bug" },
      { value: "New Task", label: "New Task" },
      { value: "Rework", label: "Rework" },
    ],
    [],
  );
  const taskStatusOptions = useMemo(
    () => [
      { value: "Not Started", label: "Not Started" },
      { value: "Complete", label: "Complete" },
    ],
    [],
  );

  /* Every team, not just the raiser's own — a support person raising a task
     needs to send it to Database, and a Database person may raise work that
     belongs to Web App. */
  const teamOptions = useMemo(
    () =>
      (Array.isArray(teamList) ? teamList : teamList?.data || []).map((t) => ({
        value: t.TeamCode,
        label: t.TeamName,
      })),
    [teamList],
  );

  // ==============================
  // 4. API CALLS
  // ==============================
  useEffect(() => {
    const queryParam = activeTab === "manual" ? 1 : 0;
    const apiStatusFilter = statusFilter.toLowerCase();
    const requestKey = JSON.stringify({
      queryParam,
      currentPage,
      pageSize,
      apiStatusFilter,
    });

    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    dispatch(
      tasksApis({
        payload: {
          isManuallyRaised: queryParam,
          currentPage,
          pageSize,
          statusFilter: apiStatusFilter,
        },
        navigate,
      }),
    );
  }, [dispatch, activeTab, currentPage, pageSize, navigate, statusFilter, reloadKey]);

  /* Coming back to this tab re-asks for the list.

     The fetch lives inside the effect above rather than in a function that
     can be called, and its de-dupe is keyed on the parameters — which are
     identical on a return visit, so clearing the key alone would not start
     anything. Bumping a counter that the effect depends on is what re-runs
     it; clearing the key is what stops the de-dupe swallowing the result. */
  useTabRefresh(() => {
    lastRequestRef.current = null;
    setReloadKey((k) => k + 1);
  });

  // Load Dropdowns — once on mount (StrictMode-safe).
  useEffectOnce(() => {
    dispatch(projectMenuApis({}));
    dispatch(queryFromApis({}));
    dispatch(teamListApis({}));
  });
  useEffect(() => {
    if (formData.project)
      dispatch(clientMenuApis({ projectCode: formData.project }));
  }, [dispatch, formData.project]);
  useEffect(() => {
    if (formData.client) {
      dispatch(mainMenuApis({ clientCode: formData.client }));
      dispatch(raisedbyDetailsApis({ clientCode: formData.client }));
    }
  }, [dispatch, formData.client]);
  useEffect(() => {
    if (formData.menu)
      dispatch(
        submenu1Apis({ clientCode: formData.client, menuCode: formData.menu }),
      );
  }, [dispatch, formData.menu]);
  useEffect(() => {
    // Skip the next-level fetch when "None" placeholder is selected.
    if (formData.submenu1 && formData.submenu1 !== "None")
      dispatch(
        submenu2Apis({
          clientCode: formData.client,
          subMenuCode1: formData.submenu1,
        }),
      );
  }, [dispatch, formData.submenu1]);
  useEffect(() => {
    if (formData.submenu2 && formData.submenu2 !== "None")
      dispatch(
        submenu3Apis({
          clientCode: formData.client,
          subMenuCode2: formData.submenu2,
        }),
      );
  }, [dispatch, formData.submenu2]);
  useEffect(() => {
    if (formData.submenu3 && formData.submenu3 !== "None")
      dispatch(
        submenu4Apis({
          clientCode: formData.client,
          subMenuCode3: formData.submenu3,
        }),
      );
  }, [dispatch, formData.submenu3]);

  // Auto-select logic
  useEffect(() => {
    if (
      formData.menu &&
      submenu1Options?.length === 1 &&
      formData.submenu1 !== submenu1Options[0].value
    )
      setFormData((p) => ({ ...p, submenu1: submenu1Options[0].value }));
  }, [submenu1Options, formData.submenu1, formData.menu]);
  useEffect(() => {
    if (
      formData.submenu1 &&
      submenu2Options?.length === 1 &&
      formData.submenu2 !== submenu2Options[0].value
    )
      setFormData((p) => ({ ...p, submenu2: submenu2Options[0].value }));
  }, [submenu2Options, formData.submenu2, formData.submenu1]);
  useEffect(() => {
    if (
      formData.submenu2 &&
      submenu3Options?.length === 1 &&
      formData.submenu3 !== submenu3Options[0].value
    )
      setFormData((p) => ({ ...p, submenu3: submenu3Options[0].value }));
  }, [submenu3Options, formData.submenu3, formData.submenu2]);
  useEffect(() => {
    if (
      formData.submenu3 &&
      submenu4Options?.length === 1 &&
      formData.submenu4 !== submenu4Options[0].value
    )
      setFormData((p) => ({ ...p, submenu4: submenu4Options[0].value }));
  }, [submenu4Options, formData.submenu4, formData.submenu3]);

  // On tab switch, reset BOTH the page and the status filter so a filter set on
  // one tab doesn't carry over and show stale results on the other.
  useEffect(() => {
    setCurrentPage(1);
    setStatusFilter("Incomplete");
  }, [activeTab]);

  // ==============================
  // 5. HANDLERS
  // ==============================
  const openAddModal = () => {
    setFormData(initialFormState);
    setEditQueryCode(null);
    setPendingImages([]);
    setRemovedImages([]);
    setIsEditMode(false);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (row) => {
    /* The images belong to QueryCode, which is the identity the child table
       keys on — QueryNo is the human-facing number and is not the same
       column. */
    setEditQueryCode(row.QueryCode || null);
    setPendingImages([]);
    setRemovedImages([]);
    const pCode = row.ProjectCode || row.ProjectID || "";
    const cCode = row.ClientCode || row.User_CompanyCode || "";
    setFormData({
      queryNo: row.QueryNo || row.QueryCode || row.queryNo || "",
      project: pCode,
      /* Prefilled on edit so reopening a task does not silently blank a
         required field and make the reader choose it again. */
      queryFrom: row.QueryFromCode || "",
      client: cCode,
      menu: row.MainMenuCode || "",
      submenu1: row.SubMenuCode || "",
      submenu2: row.SubMenuCode2 || "",
      submenu3: row.SubMenuCode3 || "",
      submenu4: row.SubMenuCode4 || "",
      raisedBy: row.Client_UserName || "",
      contactNo: row.MobileNo ? String(row.MobileNo) : "",
      taskName: row.TaskName || "",
      description: row.NatureofQuery || "",
      priority: row.Priority || "",
      queryType: row.QueryType || "",
      /* SQL hands a DATE back as an ISO instant at UTC midnight; the field
         and the payload both speak plain "YYYY-MM-DD". */
      targetDate: toDateOnly(row.TargetDate),
      taskStatus: row.Status || "Not Started",
      teamCode: row.TeamCode || "",
      isSupportTeam: false,
    });
    setIsEditMode(true);
    setErrors({});
    setShowModal(true);
  };

  /* No window.location.reload() here, and that is the fix rather than an
     omission.

     This function is called by Cancel AND by a successful save, and it already
     resets every piece of state the dialog owns — the reload was doing nothing
     the eight lines above it had not already done. What it DID do was throw
     the whole app away: every open tab closed, the sidebar scrolled back to
     the top, the running-task clock re-fetched, and pressing Cancel by mistake
     cost you the screens you had open.

     Worse, it made the save path lie. handleSubmit calls this and then
     dispatches tasksApis() to pull the list back with the new row in it —
     which never ran, because the reload tore the page down first. The list
     looked refreshed only because the browser had re-fetched everything from
     scratch. Now the dispatch is what refreshes it, which is what the code
     always said. */
  const handleCloseAndReset = () => {
    setShowModal(false);
    setFormData(initialFormState);
    setEditQueryCode(null);
    setPendingImages([]);
    setRemovedImages([]);
    setErrors({});
    setIsEditMode(false);
    setShowRaisedByMenu(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let updated = { ...prev, [name]: value };
      if (name === "client") {
        updated.menu = "";
        updated.submenu1 = "";
        updated.submenu2 = "";
        updated.submenu3 = "";
        updated.submenu4 = "";
      }
      // Changing the main menu must clear old submenu selections so a stale
      // value (or the "None" placeholder) from a previous menu isn't kept.
      if (name === "menu") {
        updated.submenu1 = "";
        updated.submenu2 = "";
        updated.submenu3 = "";
        updated.submenu4 = "";
      }
      if (name === "raisedBy") {
        const foundUser = Array.isArray(raisedByList)
          ? raisedByList.find(
            (u) => u.Client_UserName.toLowerCase() === value.toLowerCase(),
          )
          : null;
        if (foundUser) updated.contactNo = foundUser.MobileNo;
      }
      if (name === "project")
        return {
          ...initialFormState,
          project: value,
          raisedBy: prev.raisedBy,
          contactNo: prev.contactNo,
        };
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRaisedBySelect = (user) => {
    setFormData((prev) => ({
      ...prev,
      raisedBy: user.Client_UserName,
      contactNo: user.MobileNo,
    }));
    setShowRaisedByMenu(false);
    if (errors.raisedBy) setErrors((prev) => ({ ...prev, raisedBy: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;
    const req = (val, field, label) => {
      if (!val || String(val).trim().length === 0) {
        newErrors[field] = `${label || field} is required`;
        isValid = false;
      }
    };
    req(formData.project, "project");
    req(formData.queryFrom, "queryFrom", "Query From");
    req(formData.client, "client");
    req(formData.menu, "menu");
    if (submenu1Options.length > 0)
      req(formData.submenu1, "submenu1", "Submenu 1");
    req(formData.raisedBy, "raisedBy");
    req(formData.taskName, "taskName");
    req(formData.contactNo, "contactNo");
    req(formData.priority, "priority");
    req(formData.teamCode, "teamCode", "Team");

    /* These two matched nothing on the form and were enforced by the API
       anyway, so the only way to find out was to fill the whole dialog, press
       Create, and read a red toast that named a field the form had never
       marked. The rules are the server's — they are repeated here so they are
       answered where they are asked.

       Description: required by tasksCreateValidation (notEmpty). The label had
       no asterisk either; it has one now.

       Contact No: the server takes exactly ten digits and nothing else, so a
       number typed with spaces, a +91, or nine digits was accepted by the form
       and refused on submit. */
    req(formData.description, "description", "Description");

    if (formData.contactNo && !/^\d{10}$/.test(formData.contactNo.trim())) {
      newErrors.contactNo = "Contact No must be exactly 10 digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
    // "None" is the placeholder shown when a submenu has no values; send null for it.
    const cleanSubmenu = (v) => (v && v !== "None" ? v : null);
    const apiPayload = {
      queryNo: isEditMode ? formData.queryNo || null : null,
      projectCode: formData.project,
      /* Read by the API as QueryFromCode and handed to sp_Queries_AddEdit. */
      queryFromCode: formData.queryFrom,
      clientCode: formData.client,
      mainMenuCode: formData.menu,
      subMenuCode: cleanSubmenu(formData.submenu1),
      subMenuCode2: cleanSubmenu(formData.submenu2),
      subMenuCode3: cleanSubmenu(formData.submenu3),
      subMenuCode4: cleanSubmenu(formData.submenu4),
      raisedBy: formData.raisedBy,
      contactNo: formData.contactNo,
      taskName: formData.taskName,
      description: formData.description,
      priority: formData.priority,
      queryType: formData.queryType || "",
      // Empty means "none set", which the procedure reads as "leave what is
      // stored alone" rather than as a date.
      targetDate: formData.targetDate || null,
      // Number, not the raw string the select hands back — the API validator
      // rejects anything that is not an integer.
      teamCode: Number(formData.teamCode),
      ...(formData.taskStatus && { status: formData.taskStatus }),
      isSupportTeam: false,
    };
    const result = await dispatch(createTasksApis(apiPayload));
    if (createTasksApis.fulfilled.match(result)) {
      /* sp_Queries_AddEdit ends with SELECT QueryCode, so a create hands back
         the identity the images need. Read defensively: successHandler wraps
         once and axios wraps again. */
      const body = result.payload?.data ?? result.payload;
      const rows = Array.isArray(body) ? body : body?.data ?? [];
      const newCode = rows?.[0]?.QueryCode ?? null;

      /* The screenshots, applied now that the task exists and has a code —
         removals before additions, so swapping one image for another is not
         mistaken for asking for a third (see commitTaskImages).

         An edit already knows its code; a create only learns it here.

         A failure is reported and does not undo the task: the task is what was
         asked for and it is saved, and telling somebody their task failed
         because a screenshot did would be a lie they would act on. */
      const imageCode = (isEditMode ? editQueryCode : null) ?? newCode;
      if (imageCode && (pendingImages.length || removedImages.length)) {
        try {
          await commitTaskImages(imageCode, {
            removed: removedImages,
            added: pendingImages,
          });
        } catch (err) {
          toast.warning(
            isEditMode
              ? "The task was updated, but its screenshot changes did not all go through. Open it and check."
              : "The task was created, but its images could not be attached. Open the task to try again.",
          );
        }
      }

      toast.success(
        isEditMode
          ? "Task Updated Successfully!"
          : "New Task Created Successfully!",
      );
      handleCloseAndReset();
      dispatch(
        tasksApis({
          payload: {
            isManuallyRaised: activeTab === "manual" ? 1 : 0,
            currentPage,
            pageSize,
            statusFilter:
              statusFilter === "All" ? "" : statusFilter.toLowerCase(),
          },
          navigate,
        }),
      );
    }
    setIsLoading(false);
  };

  const handleExport = (data) => alert(`Exporting ${data.length} tasks...`);

  // --- COLUMNS CONFIG ---
  const columns = useMemo(() => {
    /* A note on these numbers, because 70px looked like plenty and was not:
       the width covers the cell's padding as well as its text, and that is
       16px on each side (--sk-cell-px in reportTable.css). A 70px column has
       38px for its value — enough for "#7851", nowhere near enough for
       "17 Aug 2026", which is why the date was breaking across two lines and
       setting the height of the whole row. Size these to text + 32. */
    const baseColumns = [
      {
        /* "Query No" was being truncated to "QU...". A header has less room
           than its column: 32px goes to padding and another ~18px to the ⋮
           menu button beside the label, so "QUERY NO" would have needed a
           115px column to spell itself out — absurd for a five-character
           value. The shorter label fits, and "#7851" underneath says what
           it is. */
        label: "Query",
        key: "QueryNo",
        width: "85px",
        cellClass: "nowrap-cell",
        render: (r) => (
          <span className="fw-bold text-secondary">#{r.QueryNo}</span>
        ),
      },
      {
        label: "Date",
        key: "FormattedDate",
        // "17 Aug 2026" is ~70px of text; 110 leaves it comfortable.
        width: "110px",
        type: "date",
        cellClass: "nowrap-cell",
        render: (r) => r.FormattedDate,
      },
      {
        /* Blank until somebody sets one — on this screen or, more often, on
           the Assignment screen, which will not let a task be given out
           without it. A dash here therefore means "not promised yet", not
           "missing". */
        label: "Target",
        key: "TargetDateValue",
        width: "110px",
        type: "date",
        cellClass: "nowrap-cell",
        searchValue: (r) => r.FormattedTargetDate || "",
        sortValue: (r) => r.TargetDateValue || "",
        render: (r) => <TargetDateCell value={r.TargetDate} status={r.Status} />,
      },
      {
        label: "Client",
        key: "DisplayClient",
        /* Left wrapping. "SPIN WORX" and "MARIS - II" fit on one line at this
           width, but client names run to "SPINWORX INDIA PRIVATE LIMITED" —
           that one has to wrap somewhere, and truncating it would hide which
           client the row belongs to. */
        width: "115px",
        cellClass: "text-cell-wrap",
        render: (r) => <span className="fw-medium">{r.DisplayClient}</span>,
      },
    ];

    const teamCol = {
      label: "Team",
      key: "TeamName",
      width: "110px",
      type: "select",
      filterType: "select",
      searchValue: (r) => r.TeamName || "",
      render: (r) => <TeamBadge name={r.TeamName} short={r.TeamShortName} />,
    };

    const statusCol = {
      label: "Status",
      key: "Status",
      // Wide enough for the longest label ("Not Started" / "In Progress") —
      // the cell is hard-capped at this width, so anything narrower clips the
      // badge and bleeds it over the next column.
      width: "120px",
      render: (r) => {
        let badgeClass = "bg-secondary";
        const s = r.Status ? r.Status.toLowerCase() : "";
        if (s.includes("complete")) badgeClass = "bg-success";
        else if (s.includes("progress")) badgeClass = "bg-primary";
        else if (s.includes("hold") || s.includes("pending"))
          badgeClass = "bg-warning text-dark";
        return (
          <span
            className={`badge ${badgeClass}`}
            style={{
              padding: "6px 10px",
              borderRadius: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {r.Status || "Open"}
          </span>
        );
      },
    };

    if (activeTab === "manual") {
      return [
        {
          label: "Task Name",
          key: "TaskName",
          /* Narrowed from 330. It was the widest column on the grid by a
             long way and still had room to spare — task names run to about
             "Cotton Bill Passing Report" — while the columns beside it were
             wrapping for want of twenty pixels. The long ones still wrap
             here, which is the right place for it to happen. */
          width: "250px",
          cellClass: "text-cell-wrap",
          render: (r) => (
            <span className="text-primary fw-medium">
              {r.TaskName || r.NatureofQuery}
            </span>
          ),
        },
        ...baseColumns,
        {
          label: "Raised By",
          key: "Client_UserName",
          width: "120px",
          cellClass: "nowrap-cell",
        },
        teamCol,
        statusCol,
        {
          label: "Action",
          key: "Action",
          width: "100px",
          render: (r) => (
            <button
              className="btn btn-ghost-edit d-flex align-items-center justify-content-center gap-2"
              style={{
                borderRadius: "50px",
                padding: "6px 14px",
                backgroundColor: "#eef2ff",
                border: "none",
                color: "#4f46e5",
                fontWeight: "600",
                fontSize: "0.8rem",
                width: "100%",
                transition: "all 0.2s",
              }}
              onClick={() => handleEdit(r)}
            >
              <i
                className="bi bi-pencil-fill"
                style={{ fontSize: "0.75rem" }}
              ></i>
              <span>Edit</span>
            </button>
          ),
        },
      ];
    } else {
      return [
        ...baseColumns,
        {
          label: "Nature of Query",
          key: "NatureofQuery",
          width: "350px",
          cellClass: "text-cell-wrap",
          render: (r) => (
            <span className="text-primary">{r.NatureofQuery}</span>
          ),
        },
        { label: "Raised By", key: "Client_UserName", width: "120px" },
        teamCol,
        statusCol,
      ];
    }
  }, [activeTab]);

  /* The chips count every row the screen holds; the grid gets the filtered
     ones. A completed task is never late — targetDateState reads Status and
     rules those out, which matters here because this list can show them. */
  const {
    rows: targetRows,
    counts: targetCounts,
    mode: targetMode,
    toggle: toggleTarget,
  } = useTargetDateFilter(processedData);

  // Calculate Total Records
  const totalRecords = tasksData?.data?.totalRecords || tasksData?.data?.total || processedData.length;

  return (
    <div className="report-container">
      {/* 1. HEADER - Responsive Layout */}
      <PageHeader
        icon={<i className="bi bi-list-check"></i>}
        title="Task Management"
        subtitle={
          <div
            className="d-flex align-items-center"
            style={{
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            {/* SUBTITLE */}
            <span>
              Manage and track support team tasks
            </span>

            {/* RECORDS */}
            <StatChip
              icon="bi-clipboard-data-fill"
              tone="blue"
              label="Records"
              value={totalRecords}
            />

            {/* FILTERED */}
            <StatChip
              icon="bi-funnel-fill"
              tone="orange"
              label="Filtered"
              value={isFilterActive ? filteredRecordCount : 0}
            />

            <TargetDateChips
              counts={targetCounts}
              mode={targetMode}
              onToggle={toggleTarget}
            />
          </div>
        }
        actions={
          <>
            {/* ADD TASK */}
            <button
              type="button"
              onClick={openAddModal}
              className="btn"
              style={{
                height: "36px",
                padding: "6px 14px",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "7px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
               + New
            </button>
          </>
        }
      />

      {/* 2. TABS */}
      <div className="tab-container">
        <div
          className={`tab-item ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => setActiveTab("manual")}
        >
          Manually Raised
        </div>
        <div
          className={`tab-item ${activeTab === "automatic" ? "active" : ""}`}
          onClick={() => setActiveTab("automatic")}
        >
          Automatically Raised
        </div>
      </div>

      {/* 3. TABLE WITH LOADER */}
      {
        /* Same rule as the Development screen: the skeleton is for the first
           load, not for every refresh. This list refetches after a task is
           created or edited — replacing the whole grid with a page skeleton
           each time reads as the screen reloading, and the rows it is about to
           show back are the rows already there plus one. */
        (listStatus === "loading" || isLoading) && processedData.length === 0 ? (
          <Loader isLoading={true} />
        ) : (
          <ReportTable
            data={targetRows}
            columns={columns}
            onExport={handleExport}
            stickyColumns={[""]}
            // Connect state for Filter Badge
            onDataChange={(data) => setFilteredRecordCount(data.length)}
            onFilterChange={(filters) => setIsFilterActive(filters.length > 0)}
            rowClassName={(r) => {
              const state = targetDateState(r.TargetDate, r.Status);
              return state === "overdue"
                ? "is-target-over"
                : state === "due"
                  ? "is-target-due"
                  : "";
            }}
          />
        )
      }

      {/* 4. MODALS */}
      <Modal
        show={showModal}
        onClose={handleCloseAndReset}
        title={isEditMode ? "Edit Task" : "Add New Task"}
        maxWidth="850px"
      >
        <form onSubmit={handleSubmit} className="modal-form-body p-4">
          {/* CONTEXT */}
          <div className="form-section-title">Project Context</div>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <SelectInput
                label="Project *"
                name="project"
                value={formData.project}
                onChange={handleChange}
                options={projectOptions}
                error={errors.project}
              />
            </div>
            <div className="col-md-6">
              <SelectInput
                label="Query From *"
                name="queryFrom"
                value={formData.queryFrom}
                onChange={handleChange}
                options={queryFromOptions}
                error={errors.queryFrom}
              />
            </div>
            <div className="col-md-6">
              <SelectInput
                label="Client *"
                name="client"
                value={formData.client}
                onChange={handleChange}
                options={clientOptions}
                error={errors.client}
                disabled={!formData.project}
              />
            </div>
          </div>

          {/* NAVIGATION */}
          <div className="submenu-box">
            <div className="row g-3">
              <div className="col-md-12">
                <SelectInput
                  label="Main Menu *"
                  name="menu"
                  value={formData.menu}
                  onChange={handleChange}
                  options={menuOptions}
                  error={errors.menu}
                  disabled={!formData.client}
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Submenu 1"
                  name="submenu1"
                  value={formData.submenu1}
                  onChange={handleChange}
                  options={submenu1Options}
                  required={submenu1Options.length > 0}
                  error={errors.submenu1}
                  disabled={!formData.menu}
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Submenu 2"
                  name="submenu2"
                  value={formData.submenu2}
                  onChange={handleChange}
                  options={submenu2Options}
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Submenu 3"
                  name="submenu3"
                  value={formData.submenu3}
                  onChange={handleChange}
                  options={submenu3Options}
                />
              </div>
              <div className="col-md-6">
                <SelectInput
                  label="Submenu 4"
                  name="submenu4"
                  value={formData.submenu4}
                  onChange={handleChange}
                  options={submenu4Options}
                />
              </div>
            </div>
          </div>

          {/* TASK DETAILS */}
          <div className="form-section-title">Task Details</div>
          <div className="mb-3">
            <TextInput
              label="Task Name *"
              name="taskName"
              value={formData.taskName}
              onChange={handleChange}
              error={errors.taskName}
              placeholder="Brief summary of the task..."
            />
          </div>
          <div className="mb-4">
            <label className="form-label fw-bold small text-muted text-uppercase mb-2">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control modern-textarea${
                errors.description ? " is-invalid" : ""
              }`}
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Detailed description..."
            ></textarea>
            {errors.description && (
              <div className="invalid-feedback d-block">
                {errors.description}
              </div>
            )}
          </div>

          {/* Under the description, because it is the same thought: a
              screenshot is the half of "what is wrong" that words are bad at.
              Nothing here reaches the server until this form is saved: the
              control stages what was added and dropped and hands it back. */}
          <div className="mb-4">
            <TaskImages
              queryCode={isEditMode ? editQueryCode : null}
              pending={pendingImages}
              onPendingChange={setPendingImages}
              removed={removedImages}
              onRemovedChange={setRemovedImages}
              saving={createStatus === "loading"}
            />
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <SelectInput
                label="Priority *"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                options={priorityOptions}
                error={errors.priority}
              />
            </div>
            <div className="col-md-4">
              <SelectInput
                label="Query Type"
                name="queryType"
                value={formData.queryType}
                onChange={handleChange}
                options={queryTypeOptions}
              />
            </div>
            {/* Optional on purpose. Somebody raising a query at the moment it
                is reported rarely knows when it can be done, and a required
                field here would be filled with a guess — which then travels to
                the Assignment screen looking like a commitment. Left empty, it
                is asked for there instead, by the person deciding who does the
                work. */}
            <div className="col-md-4">
              <DateInput
                label="Target Date"
                name="targetDate"
                value={formData.targetDate}
                onChange={handleChange}
                hint="Optional — asked for again before the task is assigned."
              />
            </div>
          </div>

          {/* OWNERSHIP — which team picks this up first */}
          <div className="form-section-title">Team</div>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <SelectInput
                label="Team *"
                name="teamCode"
                value={formData.teamCode}
                onChange={handleChange}
                options={teamOptions}
                error={errors.teamCode}
                placeholder="Which team handles this?"
              />
            </div>
            <div className="col-md-6 d-flex align-items-center">
              {/* Says out loud who will be able to see the task once saved —
                  the single most common mistake here is picking the wrong
                  team and then wondering where the task went. */}
              <div className="text-muted small">
                {formData.teamCode ? (
                  <>
                    <i className="bi bi-eye me-1" aria-hidden="true" />
                    Only the{" "}
                    <TeamBadge
                      name={
                        teamOptions.find((t) => t.value === formData.teamCode)
                          ?.label
                      }
                    />{" "}
                    team will see this task. It can be moved later.
                  </>
                ) : (
                  <>
                    <i className="bi bi-info-circle me-1" aria-hidden="true" />
                    The team you pick is the only one that will see this task.
                  </>
                )}
              </div>
            </div>
          </div>

          {/* REPORTER */}
          <div className="form-section-title">Reporter Information</div>
          <div className="row g-3 mb-4">
            <div className="col-md-6 position-relative">
              <div style={{ position: "relative" }}>
                <TextInput
                  label="Raised By *"
                  name="raisedBy"
                  value={formData.raisedBy}
                  onChange={handleChange}
                  error={errors.raisedBy}
                  autoComplete="off"
                  onFocus={() => setShowRaisedByMenu(true)}
                  onBlur={() =>
                    setTimeout(() => setShowRaisedByMenu(false), 200)
                  }
                  placeholder="Search user..."
                />
                {showRaisedByMenu && (
                  <div
                    className="custom-dropdown-menu"
                    style={{ marginTop: "-10px" }}
                  >
                    {filteredRaisedByList.length > 0 ? (
                      filteredRaisedByList.map((user, idx) => (
                        <div
                          key={idx}
                          className="custom-dropdown-item"
                          onClick={() => handleRaisedBySelect(user)}
                        >
                          <div className="fw-medium">
                            {user.Client_UserName}
                          </div>
                          <small
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {user.MobileNo || "No Contact"}
                          </small>
                        </div>
                      ))
                    ) : (
                      <div className="custom-dropdown-empty">
                        No users found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <TextInput
                label="Contact No *"
                name="contactNo"
                value={formData.contactNo}
                onChange={handleChange}
                error={errors.contactNo}
              />
            </div>
          </div>

          {/* FOOTER */}
          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light px-4 py-2 fw-medium text-secondary border"
              onClick={handleCloseAndReset}
              style={{ borderRadius: "8px" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary px-4 py-2 fw-medium shadow-sm"
              disabled={createStatus === "loading"}
              style={{ borderRadius: "8px", minWidth: "140px" }}
            >
              {createStatus === "loading" ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </span>
              ) : isEditMode ? (
                "Update Task"
              ) : (
                "Create Task"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div >
  );
};

export default Tasks;
